export type EmptyArray<T = unknown> = T[] & [];
export type EmptyRecord = Record<string | number, never>;
export type WhitespaceCharacter = " " | "\t" | "\n" | "\r";
export type EmptyIshString = "" | WhitespaceCharacter;
export type EmptyType = false | undefined | null | 0 | EmptyArray | EmptyRecord | EmptyIshString;
export type FilledType<T> = T extends EmptyType ? never : T;
export type GetReturnType<Type> = Type extends (...args: never[]) => infer Return ? Return : never;
export type NullishType = undefined | null;

export type Weekday = "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday";

export type WeekDates = Record<Weekday, Date>;

export type MapEntry<T extends string | number, V = string> = [T, V];

export type ExternalHref = `https://${string}` | `http://${string}`;

export enum TimeZone {
    australia_adelaide = "Australia/Adelaide",
    australia_brisbane = "Australia/Brisbane",
    australia_broken_hill = "Australia/Broken_Hill",
    australia_darwin = "Australia/Darwin",
    australia_hobart = "Australia/Hobart",
    australia_melbourne = "Australia/Melbourne",
    australia_perth = "Australia/Perth",
    australia_sydney = "Australia/Sydney",
    africa_algiers = "Africa/Algiers",
    africa_bissau = "Africa/Bissau",
    africa_cairo = "Africa/Cairo",
    africa_casablanca = "Africa/Casablanca",
    africa_ceuta = "Africa/Ceuta",
    africa_el_aaiun = "Africa/El_Aaiun",
    africa_johannesburg = "Africa/Johannesburg",
    africa_khartoum = "Africa/Khartoum",
    africa_lagos = "Africa/Lagos",
    africa_maputo = "Africa/Maputo",
    africa_monrovia = "Africa/Monrovia",
    africa_nairobi = "Africa/Nairobi",
    africa_ndjamena = "Africa/Ndjamena",
    africa_tripoli = "Africa/Tripoli",
    africa_tunis = "Africa/Tunis",
    africa_windhoek = "Africa/Windhoek",
    america_anchorage = "America/Anchorage",
    america_araguaina = "America/Araguaina",
    america_asuncion = "America/Asuncion",
    america_bahia = "America/Bahia",
    america_bahia_banderas = "America/Bahia_Banderas",
    america_barbados = "America/Barbados",
    america_belem = "America/Belem",
    america_belize = "America/Belize",
    america_bogota = "America/Bogota",
    america_boise = "America/Boise",
    america_campo_grande = "America/Campo_Grande",
    america_cancun = "America/Cancun",
    america_caracas = "America/Caracas",
    america_cayenne = "America/Cayenne",
    america_chicago = "America/Chicago",
    america_chihuahua = "America/Chihuahua",
    america_costa_rica = "America/Costa_Rica",
    america_cuiaba = "America/Cuiaba",
    america_dawson_creek = "America/Dawson_Creek",
    america_denver = "America/Denver",
    america_detroit = "America/Detroit",
    america_edmonton = "America/Edmonton",
    america_eirunepe = "America/Eirunepe",
    america_el_salvador = "America/El_Salvador",
    america_fortaleza = "America/Fortaleza",
    america_glace_bay = "America/Glace_Bay",
    america_guatemala = "America/Guatemala",
    america_guayaquil = "America/Guayaquil",
    america_guyana = "America/Guyana",
    america_halifax = "America/Halifax",
    america_havana = "America/Havana",
    america_hermosillo = "America/Hermosillo",
    america_jamaica = "America/Jamaica",
    america_juneau = "America/Juneau",
    america_la_paz = "America/La_Paz",
    america_lima = "America/Lima",
    america_los_angeles = "America/Los_Angeles",
    america_maceio = "America/Maceio",
    america_managua = "America/Managua",
    america_manaus = "America/Manaus",
    america_martinique = "America/Martinique",
    america_matamoros = "America/Matamoros",
    america_mazatlan = "America/Mazatlan",
    america_merida = "America/Merida",
    america_mexico_city = "America/Mexico_City",
    america_moncton = "America/Moncton",
    america_monterrey = "America/Monterrey",
    america_montevideo = "America/Montevideo",
    america_new_york = "America/New_York",
    america_ojinaga = "America/Ojinaga",
    america_panama = "America/Panama",
    america_paramaribo = "America/Paramaribo",
    america_phoenix = "America/Phoenix",
    america_port_au_prince = "America/Port-au-Prince",
    america_porto_velho = "America/Porto_Velho",
    america_puerto_rico = "America/Puerto_Rico",
    america_recife = "America/Recife",
    america_regina = "America/Regina",
    america_rio_branco = "America/Rio_Branco",
    america_santarem = "America/Santarem",
    america_santiago = "America/Santiago",
    america_santo_domingo = "America/Santo_Domingo",
    america_sao_paulo = "America/Sao_Paulo",
    america_st_johns = "America/St_Johns",
    america_swift_current = "America/Swift_Current",
    america_tegucigalpa = "America/Tegucigalpa",
    america_tijuana = "America/Tijuana",
    america_toronto = "America/Toronto",
    america_vancouver = "America/Vancouver",
    america_whitehorse = "America/Whitehorse",
    america_winnipeg = "America/Winnipeg",
    asia_almaty = "Asia/Almaty",
    asia_amman = "Asia/Amman",
    asia_anadyr = "Asia/Anadyr",
    asia_aqtau = "Asia/Aqtau",
    asia_aqtobe = "Asia/Aqtobe",
    asia_ashgabat = "Asia/Ashgabat",
    asia_baghdad = "Asia/Baghdad",
    asia_baku = "Asia/Baku",
    asia_bangkok = "Asia/Bangkok",
    asia_beirut = "Asia/Beirut",
    asia_bishkek = "Asia/Bishkek",
    asia_chita = "Asia/Chita",
    asia_colombo = "Asia/Colombo",
    asia_damascus = "Asia/Damascus",
    asia_dhaka = "Asia/Dhaka",
    asia_dili = "Asia/Dili",
    asia_dubai = "Asia/Dubai",
    asia_dushanbe = "Asia/Dushanbe",
    asia_gaza = "Asia/Gaza",
    asia_hebron = "Asia/Hebron",
    asia_ho_chi_minh = "Asia/Ho_Chi_Minh",
    asia_hong_kong = "Asia/Hong_Kong",
    asia_hovd = "Asia/Hovd",
    asia_irkutsk = "Asia/Irkutsk",
    asia_jakarta = "Asia/Jakarta",
    asia_jayapura = "Asia/Jayapura",
    asia_jerusalem = "Asia/Jerusalem",
    asia_kabul = "Asia/Kabul",
    asia_kamchatka = "Asia/Kamchatka",
    asia_karachi = "Asia/Karachi",
    asia_kathmandu = "Asia/Kathmandu",
    asia_kolkata = "Asia/Kolkata",
    asia_krasnoyarsk = "Asia/Krasnoyarsk",
    asia_kuching = "Asia/Kuching",
    asia_macau = "Asia/Macau",
    asia_magadan = "Asia/Magadan",
    asia_makassar = "Asia/Makassar",
    asia_manila = "Asia/Manila",
    asia_nicosia = "Asia/Nicosia",
    asia_novokuznetsk = "Asia/Novokuznetsk",
    asia_novosibirsk = "Asia/Novosibirsk",
    asia_omsk = "Asia/Omsk",
    asia_oral = "Asia/Oral",
    asia_pontianak = "Asia/Pontianak",
    asia_pyongyang = "Asia/Pyongyang",
    asia_qatar = "Asia/Qatar",
    asia_qyzylorda = "Asia/Qyzylorda",
    asia_riyadh = "Asia/Riyadh",
    asia_sakhalin = "Asia/Sakhalin",
    asia_samarkand = "Asia/Samarkand",
    asia_seoul = "Asia/Seoul",
    asia_shanghai = "Asia/Shanghai",
    asia_singapore = "Asia/Singapore",
    asia_taipei = "Asia/Taipei",
    asia_tashkent = "Asia/Tashkent",
    asia_tbilisi = "Asia/Tbilisi",
    asia_tehran = "Asia/Tehran",
    asia_thimphu = "Asia/Thimphu",
    asia_tokyo = "Asia/Tokyo",
    asia_tomsk = "Asia/Tomsk",
    asia_ulaanbaatar = "Asia/Ulaanbaatar",
    asia_urumqi = "Asia/Urumqi",
    asia_vladivostok = "Asia/Vladivostok",
    asia_yakutsk = "Asia/Yakutsk",
    asia_yekaterinburg = "Asia/Yekaterinburg",
    asia_yerevan = "Asia/Yerevan",
    atlantic_azores = "Atlantic/Azores",
    atlantic_bermuda = "Atlantic/Bermuda",
    atlantic_canary = "Atlantic/Canary",
    atlantic_cape_verde = "Atlantic/Cape_Verde",
    atlantic_faroe = "Atlantic/Faroe",
    atlantic_madeira = "Atlantic/Madeira",
    europe_andorra = "Europe/Andorra",
    europe_athens = "Europe/Athens",
    europe_belgrade = "Europe/Belgrade",
    europe_berlin = "Europe/Berlin",
    europe_brussels = "Europe/Brussels",
    europe_bucharest = "Europe/Bucharest",
    europe_budapest = "Europe/Budapest",
    europe_chisinau = "Europe/Chisinau",
    europe_dublin = "Europe/Dublin",
    europe_gibraltar = "Europe/Gibraltar",
    europe_helsinki = "Europe/Helsinki",
    europe_istanbul = "Europe/Istanbul",
    europe_kaliningrad = "Europe/Kaliningrad",
    europe_kirov = "Europe/Kirov",
    europe_lisbon = "Europe/Lisbon",
    europe_london = "Europe/London",
    europe_madrid = "Europe/Madrid",
    europe_malta = "Europe/Malta",
    europe_minsk = "Europe/Minsk",
    europe_moscow = "Europe/Moscow",
    europe_paris = "Europe/Paris",
    europe_prague = "Europe/Prague",
    europe_riga = "Europe/Riga",
    europe_rome = "Europe/Rome",
    europe_samara = "Europe/Samara",
    europe_simferopol = "Europe/Simferopol",
    europe_sofia = "Europe/Sofia",
    europe_tallinn = "Europe/Tallinn",
    europe_tirane = "Europe/Tirane",
    europe_vienna = "Europe/Vienna",
    europe_vilnius = "Europe/Vilnius",
    europe_volgograd = "Europe/Volgograd",
    europe_warsaw = "Europe/Warsaw",
    europe_zurich = "Europe/Zurich",
    indian_maldives = "Indian/Maldives",
    indian_mauritius = "Indian/Mauritius",
    pacific_apia = "Pacific/Apia",
    pacific_auckland = "Pacific/Auckland",
    pacific_bougainville = "Pacific/Bougainville",
    pacific_efate = "Pacific/Efate",
    pacific_fiji = "Pacific/Fiji",
    pacific_galapagos = "Pacific/Galapagos",
    pacific_guadalcanal = "Pacific/Guadalcanal",
    pacific_guam = "Pacific/Guam",
    pacific_honolulu = "Pacific/Honolulu",
    pacific_kwajalein = "Pacific/Kwajalein",
    pacific_norfolk = "Pacific/Norfolk",
    pacific_noumea = "Pacific/Noumea",
    pacific_palau = "Pacific/Palau",
    pacific_port_moresby = "Pacific/Port_Moresby",
    pacific_rarotonga = "Pacific/Rarotonga",
    pacific_tahiti = "Pacific/Tahiti",
    pacific_tarawa = "Pacific/Tarawa",
    pacific_tongatapu = "Pacific/Tongatapu",
}
